import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Menu, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar } from '@/components/ui/avatar';
import { NotificationBell } from '@/components/layout/NotificationBell';
import { CommandPalette } from '@/components/search/CommandPalette';
import { useAuth } from '@/lib/hooks/useAuth';
import { signOut } from '@/lib/api/auth';
import { cn } from '@/lib/utils/cn';
import { FEATURES } from '@/lib/features';

const PUBLIC_NAV = [
  { to: '/directory',  label: 'Directory',  enabled: FEATURES.directory },
  { to: '/groups',     label: 'Community',  enabled: FEATURES.community },
  { to: '/events',     label: 'Events',     enabled: FEATURES.events },
  { to: '/ventures',   label: 'Ventures',   enabled: FEATURES.ventures },
  { to: '/jobs',       label: 'Jobs',       enabled: FEATURES.jobs },
  { to: '/mentorship', label: 'Mentorship', enabled: FEATURES.mentorship },
  { to: '/map',        label: 'Map',        enabled: FEATURES.directory },
].filter((l) => l.enabled);

const AUTH_NAV = [
  { to: '/feed',       label: 'Feed',       enabled: FEATURES.feed },
  { to: '/directory',  label: 'Directory',  enabled: FEATURES.directory },
  { to: '/groups',     label: 'Community',  enabled: FEATURES.community },
  { to: '/events',     label: 'Events',     enabled: FEATURES.events },
  { to: '/ventures',   label: 'Ventures',   enabled: FEATURES.ventures },
  { to: '/jobs',       label: 'Jobs',       enabled: FEATURES.jobs },
  { to: '/mentorship', label: 'Mentorship', enabled: FEATURES.mentorship },
  { to: '/messages',     label: 'Messages',     enabled: FEATURES.messages },
  { to: '/learn-khmer', label: 'Learn Khmer',  enabled: FEATURES.learnKhmer },
].filter((l) => l.enabled);

export function AppHeader() {
  const { isAuthenticated, user, profile } = useAuth();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const navLinks = isAuthenticated ? AUTH_NAV : PUBLIC_NAV;

  const openPalette = useCallback(() => setPaletteOpen(true), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!dropdownOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDropdownOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (!dropdownRef.current?.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [dropdownOpen]);

  // Lock body scroll when the mobile drawer is open.
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [mobileOpen]);

  const handleSignOut = async () => {
    setDropdownOpen(false);
    setMobileOpen(false);
    await signOut();
    navigate('/');
  };

  const displayName = profile?.display_name ?? user?.email ?? '';
  const avatarSrc = profile?.avatar_url ?? null;
  const profileHandle = profile?.handle;

  return (
    <>
      <header className="app-glass sticky top-0 z-40 border-b border-border/40 pt-safe">
        {/* Mobile row */}
        <div className="flex h-14 items-center justify-between px-3 md:hidden">
          <button
            type="button"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((o) => !o)}
            className="flex h-10 w-10 items-center justify-center rounded-full text-navy/80 transition-transform active:scale-90"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>

          <Link to={isAuthenticated ? '/feed' : '/'} className="flex items-center gap-2">
            <img src="/brand/cpn_mark.svg" alt="" aria-hidden="true" className="h-7 w-7" />
            <span className="font-display text-[15px] italic tracking-[0.14em] text-navy">
              C&middot;P&middot;N
            </span>
          </Link>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={openPalette}
              aria-label="Search"
              className="flex h-10 w-10 items-center justify-center rounded-full text-navy/80 transition-transform active:scale-90"
            >
              <Search className="h-5 w-5" />
            </button>
            {isAuthenticated ? (
              <>
                <NotificationBell />
                <Link
                  to="/me"
                  aria-label="My profile"
                  className="flex h-10 w-10 items-center justify-center rounded-full transition-transform active:scale-90"
                >
                  <Avatar name={displayName} src={avatarSrc} size="sm" />
                </Link>
              </>
            ) : (
              <Button asChild variant="gold" size="sm">
                <Link to="/signin">Sign in</Link>
              </Button>
            )}
          </div>
        </div>

        {/* Desktop row */}
        <div className="container hidden h-16 items-center justify-between gap-4 md:flex">
          <Link
            to={isAuthenticated ? '/feed' : '/'}
            aria-label="Home"
            className="flex shrink-0 items-center"
          >
            <img src="/brand/cpn_mark.svg" alt="CPN" className="h-9 w-9" />
          </Link>

          <nav className="flex items-center gap-6">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  cn(
                    'text-sm transition-all rounded-md px-2.5 py-1 border-b-2',
                    isActive
                      ? 'font-semibold text-navy border-gold bg-navy/5'
                      : 'text-navy/60 border-transparent hover:text-navy hover:bg-navy/5 hover:border-navy/20',
                  )
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={openPalette}
              aria-label="Search (⌘K)"
              className="flex h-9 items-center gap-1.5 rounded-lg border border-border/70 bg-white px-2.5 text-muted transition-colors hover:border-border hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/40"
            >
              <Search className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="text-xs">Search</span>
              <kbd className="rounded bg-border/60 px-1 py-0.5 text-[10px] leading-none">⌘K</kbd>
            </button>

            {isAuthenticated && <NotificationBell />}
            {isAuthenticated ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => setDropdownOpen((o) => !o)}
                  aria-expanded={dropdownOpen}
                  aria-haspopup="menu"
                  aria-label="Account menu"
                  className="flex items-center gap-2 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/40"
                >
                  <Avatar name={displayName} src={avatarSrc} size="sm" />
                  <span className="max-w-[120px] truncate text-xs text-navy">
                    {displayName}
                  </span>
                </button>

                {dropdownOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 top-full mt-2 w-52 rounded-xl border border-border/70 bg-white py-1 shadow-lg"
                  >
                    {profileHandle && (
                      <DropdownItem
                        to={`/profile/${profileHandle}`}
                        onClick={() => setDropdownOpen(false)}
                      >
                        My Profile
                      </DropdownItem>
                    )}
                    <DropdownItem to="/me/edit" onClick={() => setDropdownOpen(false)}>
                      Edit Profile
                    </DropdownItem>
                    {profile?.verification === 'admin_verified' && (
                      <>
                        <div className="my-1 border-t border-border/60" />
                        <DropdownItem to="/admin" onClick={() => setDropdownOpen(false)}>
                          Admin Dashboard
                        </DropdownItem>
                      </>
                    )}
                    <div className="my-1 border-t border-border/60" />
                    <button
                      role="menuitem"
                      type="button"
                      onClick={handleSignOut}
                      className="flex w-full items-center px-4 py-2 text-sm text-ink/80 hover:bg-cream-warm hover:text-navy"
                    >
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Button asChild variant="ghost-gold" size="sm">
                  <Link to="/signin">Sign in</Link>
                </Button>
                <Button asChild variant="gold" size="sm">
                  <Link to="/signup">Join →</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            aria-hidden="true"
            onClick={() => setMobileOpen(false)}
            className="absolute inset-0 bg-navy/40"
          />
          <nav
            aria-label="Mobile navigation"
            className="absolute inset-y-0 left-0 flex w-[84%] max-w-[320px] flex-col bg-cream pt-safe pb-safe shadow-2xl"
          >
            <div className="flex h-14 items-center justify-between border-b border-border/40 px-4">
              <Link
                to={isAuthenticated ? '/feed' : '/'}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2"
              >
                <img src="/brand/cpn_mark.svg" alt="" aria-hidden="true" className="h-7 w-7" />
                <span className="font-display text-base italic tracking-[0.14em] text-navy">
                  C&middot;P&middot;N
                </span>
              </Link>
              <button
                type="button"
                aria-label="Close menu"
                onClick={() => setMobileOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-full text-navy/80 active:scale-90"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-3 py-3">
              <button
                type="button"
                onClick={() => { setMobileOpen(false); openPalette(); }}
                className="mb-3 flex w-full items-center gap-2 rounded-xl border border-border/60 bg-white px-3 py-2.5 text-left text-sm text-muted"
              >
                <Search className="h-4 w-4" aria-hidden="true" />
                <span>Search members, groups…</span>
              </button>

              <div className="flex flex-col gap-0.5">
                {navLinks.map((link) => (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'rounded-lg px-3 py-3 text-[15px] transition-colors',
                        isActive
                          ? 'bg-navy/5 font-medium text-navy'
                          : 'text-navy/75 active:bg-navy/5',
                      )
                    }
                  >
                    {link.label}
                  </NavLink>
                ))}
              </div>

              {isAuthenticated && (
                <>
                  <div className="my-3 h-px bg-border/50" />
                  <div className="flex flex-col gap-0.5">
                    {profileHandle && (
                      <NavLink
                        to={`/profile/${profileHandle}`}
                        onClick={() => setMobileOpen(false)}
                        className="rounded-lg px-3 py-3 text-[15px] text-navy/75 active:bg-navy/5"
                      >
                        My Profile
                      </NavLink>
                    )}
                    <NavLink
                      to="/me/edit"
                      onClick={() => setMobileOpen(false)}
                      className="rounded-lg px-3 py-3 text-[15px] text-navy/75 active:bg-navy/5"
                    >
                      Edit Profile
                    </NavLink>
                    <NavLink
                      to="/notifications"
                      onClick={() => setMobileOpen(false)}
                      className="rounded-lg px-3 py-3 text-[15px] text-navy/75 active:bg-navy/5"
                    >
                      Notifications
                    </NavLink>
                  </div>
                </>
              )}
            </div>

            {isAuthenticated && (
              <div className="border-t border-border/40 px-3 py-3">
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="w-full rounded-lg px-3 py-3 text-left text-[15px] text-navy/75 active:bg-navy/5"
                >
                  Sign out
                </button>
              </div>
            )}
          </nav>
        </div>
      )}
    </>
  );
}

function DropdownItem({
  to,
  onClick,
  children,
}: {
  to: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Link
      role="menuitem"
      to={to}
      onClick={onClick}
      className="flex items-center px-4 py-2 text-sm text-ink/80 hover:bg-cream-warm hover:text-navy"
    >
      {children}
    </Link>
  );
}
